import React, { useState, useEffect } from 'react';
import { BookOpen, Loader2, X, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { ProjectPlaybook } from '../../services/Project';
import { getPlaybook, Playbook, Rule } from '../../services/Playbook';
import { useI18n } from '../../i18n';

interface PlaybookViewerSheetProps {
  playbook: ProjectPlaybook | null;
  isOpen: boolean;
  onClose: () => void;
}

interface RuleItemProps {
  rule: Rule;
}

const RuleItem: React.FC<RuleItemProps> = ({ rule }) => {
  const { t } = useI18n();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div className="border rounded-lg bg-card">
        <CollapsibleTrigger asChild>
          <button className="flex items-center gap-2 w-full p-3 text-left hover:bg-muted/50 transition-colors">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            )}
            <span className="text-sm font-medium flex-1 truncate">{rule.name}</span>
            <Badge
              variant={rule.isActive ? 'default' : 'secondary'}
              className="text-xs flex-shrink-0"
            >
              {rule.isActive ? t.qagentDocument.playbookViewer.active : t.qagentDocument.playbookViewer.inactive}
            </Badge>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-3 pb-3 pt-0">
            <div className="pl-6 text-sm text-muted-foreground whitespace-pre-wrap">
              {rule.instructions}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export const PlaybookViewerSheet: React.FC<PlaybookViewerSheetProps> = ({
  playbook,
  isOpen,
  onClose,
}) => {
  const { t } = useI18n();
  const pv = t.qagentDocument.playbookViewer;
  const [fullPlaybook, setFullPlaybook] = useState<Playbook | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!playbook || !isOpen) {
      setFullPlaybook(null);
      setError(null);
      return;
    }

    const loadPlaybook = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await getPlaybook(playbook.playbookId);
        if (response.success && response.data) {
          setFullPlaybook(response.data);
        } else {
          throw new Error('Failed to load playbook');
        }
      } catch (err) {
        console.error('Error loading playbook:', err);
        setError(pv.loadFailed);
      } finally {
        setIsLoading(false);
      }
    };

    loadPlaybook();
  }, [playbook?.playbookId, isOpen]);

  if (!isOpen) return null;

  const activeRules = fullPlaybook?.rules.filter(r => r.isActive) || [];
  const inactiveRules = fullPlaybook?.rules.filter(r => !r.isActive) || [];

  return (
    <div className="h-full flex flex-col border-l bg-background">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b flex-shrink-0">
        <BookOpen className="h-4 w-4 text-brand" />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium truncate block">
            {playbook?.playbookName || pv.fallbackName}
          </span>
          {playbook?.playbookAgreementType && (
            <span className="text-xs text-muted-foreground truncate block">
              {playbook.playbookAgreementType}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 flex-shrink-0"
          onClick={onClose}
          title={pv.close}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="text-sm">{pv.loading}</span>
            </div>
          </div>
        )}

        {error && !isLoading && (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3 text-muted-foreground">
              <BookOpen className="h-8 w-8" />
              <span className="text-sm">{error}</span>
            </div>
          </div>
        )}

        {!isLoading && !error && fullPlaybook && (
          <ScrollArea className="h-full">
            <div className="p-3 space-y-4">
              {/* Summary */}
              <div className="text-xs text-muted-foreground">
                {pv.ruleCount(fullPlaybook.rules.length)}
                {activeRules.length > 0 && pv.activeCount(activeRules.length)}
              </div>

              {/* Rules List */}
              {fullPlaybook.rules.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">{pv.noRules}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Active rules first */}
                  {activeRules.map((rule) => (
                    <RuleItem key={rule.id} rule={rule} />
                  ))}
                  {/* Inactive rules */}
                  {inactiveRules.map((rule) => (
                    <RuleItem key={rule.id} rule={rule} />
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
};

export default PlaybookViewerSheet;
